// The escalations behind ONE person's Escalations count in the Team Metric table — opened by
// clicking that number, or a single reason in their top-3 list (which pre-filters to that issue).
// Same shape as the other Control Tower drill-downs: a nullable request object drives it, the
// response `count` equals the clicked tally.
import { Drawer, Pagination, Table, Tag, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import React, { useEffect, useState } from "react";
import FlagPills from "./flag_pills";
import {
  ControlTowerBoardLane,
  TeamEscalationRow,
  listTeamEscalations,
} from "../control_tower.api";
import styles from "../index.module.scss";

// Which person, and optionally which single reason, the list is scoped to.
export interface TeamEscalationRequest {
  userId: string;
  name: string;
  reason?: string;
}

interface Props {
  request: TeamEscalationRequest | null;
  boardLane: ControlTowerBoardLane;
  dateFrom: string;
  dateTo: string;
  onClose: () => void;
}

const dash = <span style={{ color: "#bbb" }}>—</span>;

// The queue an escalation was raised from. A GRN / pick row books no truck, so it can only be
// named by its leg — that's why they carry no booking id.
const LEG_LABEL: Record<TeamEscalationRow["leg"], string> = {
  booking: "Truck",
  receive: "GRN",
  pick: "Pick list",
};

const fmtWhen = (v: string | null) =>
  v
    ? new Date(v).toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

const columns: ColumnsType<TeamEscalationRow> = [
  {
    title: "Raised",
    dataIndex: "raised_at",
    key: "raised_at",
    width: 140,
    render: fmtWhen,
  },
  {
    title: "Problem",
    dataIndex: "issue_type",
    key: "issue_type",
    render: (v: string, r) => (
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <span style={{ fontWeight: 600 }}>{v}</span>
        {r.category && <span style={{ fontSize: 12, color: "#8c8c8c" }}>{r.category}</span>}
      </div>
    ),
  },
  {
    title: "PO Number",
    dataIndex: "po_number",
    key: "po_number",
    render: (v: string | null, r) => (
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <span>{v ?? "—"}</span>
        <FlagPills express={r.express} />
      </div>
    ),
  },
  { title: "Lead ID", dataIndex: "lead_id", key: "lead_id", render: (v) => v ?? dash },
  {
    title: "Raised on",
    dataIndex: "leg",
    key: "leg",
    width: 110,
    render: (v: TeamEscalationRow["leg"]) => <Tag>{LEG_LABEL[v] ?? v}</Tag>,
  },
  {
    title: "Remarks",
    dataIndex: "remarks",
    key: "remarks",
    render: (v: string) =>
      v ? (
        <Tooltip title={v} overlayStyle={{ maxWidth: 480 }}>
          <span className={styles.escRemarks}>{v}</span>
        </Tooltip>
      ) : (
        dash
      ),
  },
  {
    title: "State",
    dataIndex: "resolved",
    key: "resolved",
    width: 100,
    render: (v: boolean) =>
      v ? <Tag color="green">Resolved</Tag> : <Tag color="gold">Open</Tag>,
  },
];

const TeamEscalationDrawer: React.FC<Props> = ({ request, boardLane, dateFrom, dateTo, onClose }) => {
  const [rows, setRows] = useState<TeamEscalationRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);

  // A new drill-down resets to page 1 (kept out of the fetch effect so paging doesn't reset itself).
  useEffect(() => {
    setPage(1);
  }, [request]);

  useEffect(() => {
    if (!request) return;
    let cancelled = false;
    setLoading(true);
    // The window is sent under both names — the backend reads whichever its board_lane scopes on.
    listTeamEscalations({
      user_id: request.userId,
      board_lane: boardLane,
      reason: request.reason,
      delivery_from: dateFrom,
      delivery_to: dateTo,
      dispatch_from: dateFrom,
      dispatch_to: dateTo,
      page,
      page_size: pageSize,
    }).then((res) => {
      if (cancelled) return;
      setRows(res?.results ?? []);
      setTotal(res?.count ?? 0);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [request, boardLane, dateFrom, dateTo, page, pageSize]);

  const title = request
    ? `${request.name} — Escalations${request.reason ? ` · ${request.reason}` : ""}`
    : "";

  return (
    <Drawer title={title} width={1000} open={!!request} onClose={onClose} destroyOnClose>
      <Table
        rowKey="id"
        size="small"
        loading={loading}
        columns={columns}
        dataSource={rows}
        pagination={false}
        scroll={{ x: 900 }}
      />
      <div style={{ marginTop: 16, textAlign: "right" }}>
        <Pagination
          current={page}
          pageSize={pageSize}
          total={total}
          showSizeChanger
          pageSizeOptions={["20", "50", "100", "200"]}
          showTotal={(t) => `${t} escalation${t === 1 ? "" : "s"}`}
          onChange={(p, size) => {
            setPage(p);
            setPageSize(size);
          }}
        />
      </div>
    </Drawer>
  );
};

export default TeamEscalationDrawer;
